import React from "react";
import { Button } from "../../../../components/Buttons/Button/Button";
import { NoHrefLink } from "../../../../components/Links/NoHrefLink/NoHrefLink";
import { ShimmerLine } from "../../../../components/Shimmer/ShimmerLine/ShimmerLine";
import { validateProps } from "../../../../shared/props";
import { makeStyles, mergeClasses } from "../../../../styles/makeStyles";
import {
    ComparisonFeatureLayout,
    ComparisonFeatureLayoutProps,
    ComparisonFeatureProps,
} from "../ComparisonFeature";

export interface ComparisonFeatureShimmerProps {
    /** Whether to show a badge in this shimmer */
    readonly badge?: boolean;
    /**
     * Whether and how to show an action section in this shimmer. Defaults to
     * showing a single button shimmer if actions props are not passed in.
     * True to show multiple action shimmers. Action object to show without shimmer.
     */
    readonly actions?: boolean | ComparisonFeatureProps["actions"];
    /** Number of features to show shimmering in the card */
    readonly numberOfChildren?: number;
    /** Style variants for this Shimmer */
    readonly variants?: ComparisonFeatureProps["variants"];
}

// For future consideration: Consider if there is an easier way to center ShimmerLines
const useShimmerClasses = makeStyles({
    badge: {
        flex: "1",
        margin: "-5px -24px",
    },
    title: {
        display: "flex",
        marginTop: "5px",
        marginBottom: "12px",

        "> *": {
            flex: "0 0 70%",
        },
    },
    titleCenter: {
        justifyContent: "center",
    },
    subtitle: {
        display: "flex",

        "> *": {
            flex: "0 0 40%",
        },
    },
    subtitleCenter: {
        justifyContent: "center",
    },
    mediaShimmer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "22px",
        padding: "16px 0",

        " :nth-child(2)": {
            flex: "1 1 auto",
        },
    },
    actions: {
        display: "flex",
        "> *": {
            flex: "0 0 150px",
        },
    },
    actionsCenter: {
        justifyContent: "center",
    },
});

const MediaShimmer: React.FC = () => {
    const classes = useShimmerClasses();
    return (
        <div className={classes.mediaShimmer}>
            <ShimmerLine width="12px" height={12} />
            <ShimmerLine height={8} />
        </div>
    );
};

const defaultNumberOfChildren = 4;

// tslint:disable jsx-wrap-multiline
type ComparisonFeatureShimmerFC = React.FC<ComparisonFeatureShimmerProps>;
export const ComparisonFeatureShimmer: ComparisonFeatureShimmerFC = ({
    badge,
    actions,
    numberOfChildren = defaultNumberOfChildren,
    variants,
    ...otherProps
}) => {
    validateProps("ComparisonFeatureShimmer", otherProps as any);

    const classes = useShimmerClasses();
    const isCenterVariant = variants?.alignment === "center";

    const titleClass = mergeClasses(
        classes.title,
        isCenterVariant && classes.titleCenter
    );

    const subtitleClass = mergeClasses(
        classes.subtitle,
        isCenterVariant && classes.subtitleCenter
    );

    const actionsClass = mergeClasses(
        classes.actions,
        isCenterVariant && classes.actionsCenter
    );

    let actionsShimmer: React.ReactNode[] = [];
    if (typeof actions === "object") {
        actionsShimmer = [
            ...(!!actions.cta ? [<Button {...actions.cta} disabled={true} />] : []),
            ...(!!actions.alt ? [<NoHrefLink {...actions.alt} disabled={true} />] : []),
        ];
    } else if (actions) {
        actionsShimmer = [(
            <div className={actionsClass}>
                <ShimmerLine height={32} />
            </div>
        ), (
            <div className={actionsClass}>
                <ShimmerLine height={12} />
            </div>
        )];
    } else {
        // Default is one action shimmer
        actionsShimmer = [(
            <div className={actionsClass}>
                <ShimmerLine height={32} />
            </div>
        )];
    }

    const features: ComparisonFeatureLayoutProps["features"] = Array.from({
        length: numberOfChildren,
    }).map((_, i) => ({ child: <MediaShimmer key={i} /> }));

    return (
        <ComparisonFeatureLayout
            {...otherProps}
            badge={
                badge && (
                    <div className={classes.badge}>
                        <ShimmerLine height={30} width="100%" />
                    </div>
                )
            }
            title={
                <div className={titleClass}>
                    <ShimmerLine height={12} />
                </div>
            }
            subtitle={
                <div className={subtitleClass}>
                    <ShimmerLine height={12} />
                </div>
            }
            features={features}
            actions={actionsShimmer}
            variants={variants}
        />
    );
};
